/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";

// react-router-dom components
import { useNavigate, Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import { styled } from "@mui/system";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// sweetalert2 components
import Swal from "sweetalert2";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Skill90DashboardNavbar";
import DataTable from "Tables/DataTable";
import CreatePlatform from "Modals/CreatePlatform";
import CreatePlatformWizard from "Modals/CreatePlatformWizard";
import CheckObjectDeletion from "Modals/CheckObjectDeletion";

// Soft UI Dashboard PRO React components
import SoftBadge from "components/SoftBadge";

// ProductsList page components
import ActionCell from "views/platforms/platforms-list/components/ActionCell";
import SignalsCell from "views/platforms/platforms-list/components/SignalsCell";

import { getData, postData, delData } from "Utils/server";
import { getAuthorizationHeader, getUserData, setUserData } from "Utils/helpers";

import { useSoftUIController, setPlatform } from "context";
import { putData } from "Utils/server";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
  },
}));

/**
 *
 * @returns
 */
function PlatformsList() {
  const HIGHER_ROLE = 49;
  const MASTER_ROLE = 1;

  const abortController = new AbortController();

  const [controller, dispatch] = useSoftUIController();
  const { platform, permissions, sports, providers } = controller;

  const [showCreateModal, setCreateModal] = useState(false);
  const [showCreateWizardModal, setCreateWizardModal] = useState(false);
  const [showDeleteModal, setDeleteModal] = useState(false);
  const [modalCheckAction, setModalCheckAction] = useState("Delete");
  const [updateRecords, setUpdateRecords] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(0);
  const [platformNameToEdit, setPlatformNameToEdit] = useState(null);
  const [platformExchangeNameToEdit, setPlatformExchangeNameToEdit] = useState(null);
  const [vlcourtEmailToEdit, setVlcourtEmailToEdit] = useState(null);
  const [vlcourtPasswordToEdit, setVlcourtPasswordToEdit] = useState(null);
  const [queueIdentifierToEdit, setQueueIdentifierToEdit] = useState(null);
  const [routingKeyToEdit, setRoutingKeyToEdit] = useState(null);
  const [rmqsSelectedToEdit, setRmqsSelectedToEdit] = useState([]);
  const [settleOnlyBetsValue, setSettleOnlyBetsValue] = useState(null);
  const [validateBetsValue, setValidateBetsValue] = useState(null);
  const [receiveSeperateStatisticsValue, setReceiveSeperateStatisticsValue] = useState(null);
  const [riskManagementValue, setRiskManagementValue] = useState(null);
  const [demoValue, setDemoValue] = useState(null);
  const [hardProviderDownValue, setHardProviderDownValue] = useState(null);
  const [nuRemoveValue, setNuRemoveValue] = useState(null);
  const [compressMessageValue, setCompressMessageValue] = useState(null);
  const [traderViewValue, setTraderViewValue] = useState(null);
  const [promosValue, setPromosValue] = useState(null);
  const [outrightsValue, setOutrightsValue] = useState(3);
  const [logo, setLogo] = useState(null);
  const [masterId, setMasterId] = useState(null);
  const [newName, setNewName] = useState("");
  const [templateCreatedId, setTemplateCreatedId] = useState(0);
  const [allowCreate, setAllowCreate] = useState(false);
  const [allowDelete, setAllowDelete] = useState(false);
  const [allowBlockBets, setAllowBlockBets] = useState(false);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [loading, setLoading] = useState(true);
  const [availableOutrights, setAvailableOutrights] = useState([
    { value: 1, label: "NO Outrights" },
    { value: 2, label: "ONLY Outrights" },
    { value: 3, label: "ALL" },
  ]);
  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "id", accessor: "id", width: "100px" },
      { Header: "name", accessor: "name", width: "100px", Cell: makeLinkCell },
      { Header: "providers", accessor: "providers", Cell: makerollover, width: "120px" },
      { Header: "sports", accessor: "sports", Cell: makerollover, width: "120px" },
      { Header: "competitions", accessor: "competitions", width: "120px" },
      { Header: "status", accessor: "status", width: "120px", Cell: makeStatusCell },
      { Header: "feed signals", accessor: "signals", width: "120px" },
      { Header: "actions", accessor: "action", width: "120px" },
    ],

    rows: [],
  });

  const customPlatformSorting = {
    56: 1,
    12: 2,
    5: 3,
    121: 4,
    119: 5,
    47: 6,
    17: 7,
    48: 8,
  };

  function makerollover({ value, ...rest }) {
    let listItems = value; //.split(",");

    return (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {/* <div style={{ display: "block" }}> */}
              {listItems.map((item, index) => {
                return (
                  <div key={index} style={{ width: "150px", display: "inline-flex" }}>
                    {item}
                  </div>
                );
              })}
              {/* </div> */}
            </div>
          </React.Fragment>
        }
      >
        <Button>
          {value.length}
          <span style={{ paddingLeft: "5px" }}> </span>

          <Icon>info_outlined</Icon>
        </Button>
      </HtmlTooltip>
    );
  }

  function changePlatform(e, rowData) {
    e.preventDefault();
    setUserData({
      key: "defaultPlatform",
      value: {
        label: rowData.row.original.name,
        value: rowData.row.original.id,
        logo: rowData.row.original.logo,
        sorting: rowData.row.original.sorting,
        odds_styles: rowData.row.original.odds_styles,
      },
    });
    setPlatform(dispatch, {
      label: rowData.row.original.name,
      value: rowData.row.original.id,
      logo: rowData.row.original.logo,
      sorting: rowData.row.original.sorting,
      odds_styles: rowData.row.original.odds_styles,
    });
    routeChange(`/feed-settings/templates`, {});
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeLinkCell({ value, ...rest }) {
    const platformSwitches = [
      { label: "Settle Only Bets", value: rest.row.original.settle_only_bets },
      { label: "Validate Bets", value: rest.row.original.validate_bets },
      {
        label: "Receive Separate Statistics",
        value: rest.row.original.receive_seperate_statistics,
      },
      { label: "Risk Management", value: rest.row.original.risk_management },
      { label: "Demo Feed", value: rest.row.original.demo },
      { label: "Hard Provider Down", value: rest.row.original.hard_provider_down },
      { label: "NU Remove", value: rest.row.original.nu_remove },
      { label: "Compress Message", value: rest.row.original.compress_message },
      { label: "Trader View", value: rest.row.original.trader_view },
      { label: "Promos", value: rest.row.original.promos },
    ];

    const isSwitchEnabled = (switchValue) =>
      switchValue === true ||
      switchValue === 1 ||
      switchValue === "1" ||
      switchValue === "true";

    return (
      <HtmlTooltip
        placement="right"
        title={
          <React.Fragment>
            <div style={{ display: "flex", flexWrap: "wrap", rowGap: "8px" }}>
              {platformSwitches.map((item) => {
                const enabled = isSwitchEnabled(item.value);

                return (
                  <div
                    key={item.label}
                    style={{
                      width: "230px",
                      display: "inline-flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingRight: "16px",
                    }}
                  >
                    <span>{item.label}</span>
                    <Icon color={enabled ? "success" : "error"} fontSize="small">
                      {enabled ? "check_circle" : "cancel"}
                    </Icon>
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        }
      >
        <Link to={`/feed-settings/templates`} onClick={(e) => changePlatform(e, rest)}>
          <SoftTypography
            component="span"
            variant="button"
            fontWeight="regular"
            color={"dark"}
            opacity={0.8}
            sx={{ lineHeight: 0 }}
          >
            {value}
          </SoftTypography>
        </Link>
      </HtmlTooltip>
    );
  }

  function makeList({ value, ...rest }) {
    if (!value) return <></>;
    let listItems = value;

    return (
      <>
        {listItems.map((item) => {
          return (
            <SoftBadge
              style={{ marginRight: "3px" }}
              key={item}
              variant="gradient"
              color={item === "inplay" ? "info" : "secondary"}
              size="xs"
              badgeContent={item}
              container
            />
          );
        })}
      </>
    );
  }

  let timerInterval;

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeFiltersCell({ value, ...rest }) {
    return value === "" ? "None Filter Added" : value;
  }

  /**
   *
   * @param {*} param0
   * @returns
   */
  function makeStatusCell({ value, ...rest }) {
    return value === "Enabled" ? (
      <SoftButton
        variant="contained"
        size="small"
        className="badge-success"
        onClick={(e) => handleStatusData(e, rest.row.original)}
      >
        Enabled
      </SoftButton>
    ) : (
      <SoftButton
        variant="contained"
        size="small"
        className="badge-error"
        onClick={(e) => handleStatusData(e, rest.row.original)}
      >
        Disabled
      </SoftButton>
    );
  }

  /**
   *
   */
  const handleCreateRecord = () => {
    setRecordToDelete(0);
    setPlatformNameToEdit(null);
    setPlatformExchangeNameToEdit(null);
    setVlcourtEmailToEdit(null);
    setVlcourtPasswordToEdit(null);
    setQueueIdentifierToEdit(null);
    setRoutingKeyToEdit(null);
    setRmqsSelectedToEdit([]);
    setSettleOnlyBetsValue(null);
    setValidateBetsValue(null);
    setReceiveSeperateStatisticsValue(null);
    setRiskManagementValue(null);
    setHardProviderDownValue(null);
    setNuRemoveValue(null);
    setCompressMessageValue(null);
    setTraderViewValue(null);
    setPromosValue(null);
    setOutrightsValue(3);
    setLogo(null);
    setMasterId(null);
    setCreateModal(true);
  };

  const handleCreateWizardRecord = () => {
    setCreateWizardModal(true);
  };

  /**
   *
   */
  const handleOpenEditModal = (
    e,
    id,
    name,
    exchangeName,
    vlcourtEmail,
    vlcourtPassword,
    queueIdentifier,
    routingKey,
    rmqsSelected,
    settleonlybets,
    validatebets,
    receiveseperatestatistics,
    riskManagement,
    demo,
    hardProviderDown,
    nuRemove,
    compressMessage,
    traderView,
    promos,
    outrights,
    logo,
    masterId
  ) => {
    setRecordToDelete(id);
    setPlatformNameToEdit(name);
    setPlatformExchangeNameToEdit(exchangeName);
    setVlcourtEmailToEdit(vlcourtEmail);
    setVlcourtPasswordToEdit(vlcourtPassword);
    setQueueIdentifierToEdit(queueIdentifier);
    setRoutingKeyToEdit(routingKey);
    setRmqsSelectedToEdit(rmqsSelected);
    setSettleOnlyBetsValue(settleonlybets);
    setValidateBetsValue(validatebets);
    setReceiveSeperateStatisticsValue(receiveseperatestatistics);
    setRiskManagementValue(riskManagement);
    setDemoValue(demo);
    setHardProviderDownValue(hardProviderDown);
    setNuRemoveValue(nuRemove);
    setCompressMessageValue(compressMessage);
    setTraderViewValue(traderView);
    setPromosValue(promos);
    setOutrightsValue(outrights);
    setLogo(logo);
    setMasterId(masterId);
    setCreateModal(true);
  };

  const handleStatusData = (e, rowData) => {
    e.preventDefault();
    const userData = getUserData();
    let roles = userData.roles.sort((a, b) => (a.id > b.id ? 1 : -1));
    let higherRole = roles[0].id;
    if (higherRole > HIGHER_ROLE) {
      return;
    }

    if (!allowDelete && higherRole != MASTER_ROLE) return;

    setRecordToDelete(rowData.id);
    setModalCheckAction("Update");
    setDeleteModal(true);
  };

  /**
   *
   */
  const handleKeyData = (e, apikey) => {
    //console.log("Copy ApiKey: ", apikey);
    copyToClipboard(apikey, "main");
  };

  /**
   *
   */
  const handleStatsKeyData = (e, apikey) => {
    //console.log("Copy ApiKey: ", apikey);
    copyToClipboard(apikey, "stats");
  };

  const copyToClipboard = (textToCopy, type) => {
    setApiresponse("success");
    setApiResponseMsg(
      type == "main"
        ? "API-KEY has been copied to the clipboard!"
        : "STATS-API-KEY has been copied to the clipboard!"
    );
    setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  /**
   *
   */
  const handleSnapshotRequest = async (e, apikey) => {
    //console.log("SnapshotRequest: ", name);
    const userData = getUserData();
    let userId = userData.id;
    let data = {
      userId: userId,
    };
    let snapshotEndpoint = `/api/v1/signals/requestSnapshot`;
    let response = await postData(
      snapshotEndpoint,
      data,
      {
        Authorization: getAuthorizationHeader(),
        "x-api-key": apikey,
      },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response) {
      //console.log("snapshot send");
      if (response.status == "200") {
        setApiresponse("success");
        setApiResponseMsg("SNAPSHOT Signal has been sent!");
        setNotification(true);
      } else {
        setApiresponse("error");
        setApiResponseMsg("Something goes wrong try again please!");
        setNotification(true);
      }
    } else {
      setApiresponse("error");
      setApiResponseMsg("Something goes wrong try again please!");
      setNotification(true);
    }
  };

  const handleFeedRequest = async (e, action, platformId, apikey) => {
    //console.log("Feed Action: ", action);
    let actionEndpoint =
      action == "stop"
        ? `/api/v1/signals/stopConsuming/${platformId}`
        : `/api/v1/signals/startConsuming/${platformId}`;
    let response = await getData(
      actionEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response) {
      //console.log(action, "Feed Signal sent");
      if (response.status == "200") {
        setApiresponse("success");
        setApiResponseMsg(action.toUpperCase() + " Signal has been sent!");
        setNotification(true);
      } else {
        setApiresponse("error");
        setApiResponseMsg("Something goes wrong try again please!");
        setNotification(true);
      }
    } else {
      setApiresponse("error");
      setApiResponseMsg("Something goes wrong try again please!");
      setNotification(true);
    }
  };

  const handleUpdateBlockBetsRequest = async (e, blockBetFlag, platformId) => {
    let data = {
      blockBets: blockBetFlag,
      platformId: platformId,
    };
    let endPoint = "/api/v1/platforms/blockBets";

    Swal.fire({
      title: blockBetFlag === false ? `Block Bets!` : `Unblock Bets`,
      html:
        blockBetFlag === false
          ? "You are going to BLOCK all the incoming bets to this Platform.</br></br>"
          : "You are going to UNBLOCK all the incoming bets to this Platform.</br></br>",
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: blockBetFlag === false ? "Yes, block them!" : "Yes, unblock them!",
      denyButtonText: "Cancel",
      icon: "warning",
      confirmButtonColor: "#87cb16",
      denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        let response = await postData(endPoint, data, { Authorization: getAuthorizationHeader() });
        if (response) initData();
      }
    });
  };

  /**
   *
   * @param {*} e
   * @param {*} id
   */
  const handleOpenModal = (e, id) => {
    e.preventDefault();
    setRecordToDelete(id);
    setModalCheckAction("Delete");
    setDeleteModal(true);
  };

  /**
   *
   * @param {*} update
   * @param {*} name
   */
  const handleCloseModal = async (update, name) => {
    setRecordToDelete(0);
    setPlatformNameToEdit(null);
    setPlatformExchangeNameToEdit(null);
    setVlcourtEmailToEdit(null);
    setVlcourtPasswordToEdit(null);
    setQueueIdentifierToEdit(null);
    setRoutingKeyToEdit(null);
    setRmqsSelectedToEdit([]);
    setLogo(null);
    setMasterId(null);
    if (update) {
      initData();
    } else {
      //console.log("Creation canceled, do nothing!");
    }
    setCreateModal(false);
    if (update) {
      setNewName(name);
      // fetchData();
    }
  };

  const handleCloseWizardModal = async (update, name) => {
    setCreateWizardModal(false);
    if (update) {
      initData();
      setNewName(name);
    }
  };

  /**
   *
   * @param {*} update
   * @param {*} id
   */
  const handleDeleteCloseModal = async (update, id) => {
    if (update) {
      //console.log("Delete confirmed, please Update!");
      let setTemplatesEndpoint = `/api/v1/platforms/${id}`;
      let response = null;
      if (modalCheckAction == "Delete") {
        response = await delData(setTemplatesEndpoint, {
          Authorization: getAuthorizationHeader(),
        });
      } else if (modalCheckAction == "Update") {
        setTemplatesEndpoint = `/api/v1/platforms/${id}/status`;
        let platform_to_disable = dataTableData.rows.find((p) => p.id == id);
        response = await putData(
          setTemplatesEndpoint,
          { status: !platform_to_disable.originalStatus },
          {
            Authorization: getAuthorizationHeader(),
          }
        );
      }

      if (response) {
        if (modalCheckAction == "Delete") deleteRecord(id);
        initData();
      }
    } else {
      //console.log("Delete canceled, do nothing!");
    }
    setDeleteModal(false);
  };

  /**
   *
   */
  async function initData() {
    if (!platform || platform == "" || !sports || !providers) return;
    setLoading(true);
    const userData = getUserData();
    let userId = userData.id;
    let allowBlockBetsFlag = false;

    let roles = userData.roles.sort((a, b) => (a.id > b.id ? 1 : -1));
    let higherRole = roles[0].id;
    if (higherRole < HIGHER_ROLE) {
      setAllowCreate(true);
      setAllowBlockBets(true);
      allowBlockBetsFlag = true;
    }

    if (higherRole == MASTER_ROLE) setAllowDelete(true);

    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/users/${userId}/platforms`;

    let response = await getData(
      getTemplatesEndpoint,
      { Authorization: getAuthorizationHeader() },
      null,
      abortController ? { signal: abortController.signal } : null
    );
    if (response != null) {
      response.data.map((p) => {
        if (customPlatformSorting[p.id]) p.sorting = customPlatformSorting[p.id];
        else p.sorting = p.id;
      });

      response.data = response.data.sort((a, b) => a.sorting - b.sorting);

      let updatedPlatforms =
        higherRole < HIGHER_ROLE
          ? response.data.map((el) => {
              if (el.odds_styles) el.odds_styles.unshift("decimal");
              return {
                id: el.id,
                name: el.name,
                logo: el.logo,
                sorting: el.sorting,
                odds_styles: el.odds_styles,
              };
            })
          : response.data
              .filter((p) => p.status)
              .map((el) => {
                if (el.odds_styles) el.odds_styles.unshift("decimal");
                return {
                  id: el.id,
                  name: el.name,
                  logo: el.logo,
                  sorting: el.sorting,
                  odds_styles: el.odds_styles,
                };
              });

      setUserData({ key: "platforms", value: updatedPlatforms });

      response.data.map((el) => {
        let platformProviders = [];

        for (let index = 0; index < el.providers.length; index++) {
          const p = providers.find((s) => s.value == el.providers[index]);
          platformProviders.push(p.label);
        }
        el.providers = el.providers ? platformProviders : "-";
        let platformSports = [];
        for (let index = 0; index < el.sports.length; index++) {
          const sp = sports.find((s) => s.id == el.sports[index]);
          if (!sp) continue;
          platformSports.push(sp.name);
        }
        el.vl_email = el.vl_email ? el.vl_email : " ";
        el.vl_password = el.vl_password ? el.vl_password : " ";
        el.sports = el.sports ? platformSports : "-";
        el.competitions = el.competitions_count ? el.competitions_count : "-";
        el.originalStatus = el.status;
        el.status = el.status ? "Enabled" : "Disabled";
        el.allowDelete = allowDelete ? true : higherRole == MASTER_ROLE;
        el.signals =
          el.originalStatus || higherRole < HIGHER_ROLE ? (
            <SignalsCell
              id={el.id}
              el={el}
              handleKeyAction={(e) => handleKeyData(e, el.api_key)}
              handleSnapshotAction={(e) => handleSnapshotRequest(e, el.api_key)}
              handleFeedAction={(e, action) => handleFeedRequest(e, action, el.id, el.api_key)}
              handleUpdateBlockBetsAction={
                higherRole < HIGHER_ROLE
                  ? (e) => handleUpdateBlockBetsRequest(e, el.block_bets, el.id, el.api_key)
                  : null
              }
              allowBlockBets={allowBlockBetsFlag}
            />
          ) : (
            <div></div>
          );
        el.outrights = el.outrights
          ? availableOutrights.find((ao) => ao.value == el.outrights)?.value
          : availableOutrights.find((ao) => ao.value == 3)?.value;
        el.action =
          el.originalStatus || higherRole < HIGHER_ROLE ? (
            <ActionCell
              id={el.id}
              el={el}
              handleDeleteAction={
                higherRole < HIGHER_ROLE && el.allowDelete ? (e) => handleOpenModal(e, el.id) : null
              }
              handleEditAction={(e) =>
                handleOpenEditModal(
                  e,
                  el.id,
                  el.name,
                  el.exchange_name,
                  el.vl_email,
                  el.vl_password,
                  el.queue_identifier,
                  el.routing_key,
                  el.rmqs_selected,
                  el.settle_only_bets,
                  el.validate_bets,
                  el.receive_seperate_statistics,
                  el.risk_management,
                  el.demo,
                  el.hard_provider_down,
                  el.nu_remove,
                  el.compress_message,
                  el.trader_view,
                  el.promos,
                  el.outrights,
                  el.logo,
                  el.masterTemplateId
                )
              }
              handleStatsKeyAction={(e) => handleStatsKeyData(e, el.stats_api_key)}
              handleKeyAction={(e) => handleKeyData(e, el.api_key)}
            />
          ) : (
            <div></div>
          );
      });
    } else {
    }

    let data = { ...dataTableData };
    data.rows = response && response.data ? response.data : data.rows;

    data.rows.sort((a, b) => b.id - a.id);

    //console.log("Init Data");
    setDataTableData(data);
    setLoading(false);
  }

  /**
   *
   * @returns
   */
  function fetchData() {
    //console.log("updateRecords: ", updateRecords);
    if (!dataTableData.rows) return;
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];
    let addedTemplate = {
      id: initDataRows.length + 1,
      template: newName,
      filters: "",
      status: "Disabled",
      action: (
        <ActionCell
          id={initDataRows.length + 1}
          handleDeleteAction={(e) => handleOpenModal(e, initDataRows.length + 1)}
        />
      ),
    };

    if (updateRecords) {
      //console.log("initData.rows 1: ", initData.rows.length);
      initDataRows.push(addedTemplate);
      initDataRows.sort((a, b) => b.id - a.id);
      initData.rows = initDataRows;
      //console.log("initData.rows 2: ", initData.rows.length);
      setDataTableData(initData);
      setTemplateCreatedId(initDataRows.length + 1);
      setNewName("");
      showAlert();
    }
    setUpdateRecords(false);
  }

  /**
   *
   * @param {*} id
   */
  function deleteRecord(id) {
    //console.log("deleteRecord:", id);
    let initData = { ...dataTableData };
    let initDataRows = [...dataTableData.rows];

    let indexToRemove = initDataRows.findIndex((template) => template.id === id);
    initDataRows.splice(indexToRemove, 1);

    initData.rows = initDataRows;
    setDataTableData(initData);

    let userData = getUserData();
    if (userData.defaultPlatform.value === id) {
      let firstPlatform = userData.platforms[0];
      setUserData({
        key: "defaultPlatform",
        value: {
          value: firstPlatform.id,
          label: firstPlatform.name,
          logo: firstPlatform.logo,
          sorting: firstPlatform.sorting,
          odds_styles: firstPlatform.odds_styles,
        },
      });
    }
  }

  /**
   *
   * @returns
   */
  const showAlert = () =>
    Swal.fire({
      title: "You are about to redirect automatically to Template Edit Page!",
      html: "in <b></b> milliseconds.",
      timer: 5000,
      timerProgressBar: true,
      didOpen: () => {
        Swal.showLoading();
        timerInterval = setInterval(() => {
          const content = Swal.getHtmlContainer();
          if (content) {
            const b = content.querySelector("b");
            if (b) {
              b.textContent = Swal.getTimerLeft();
            }
          }
        }, 100);
      },
      willClose: () => {
        clearInterval(timerInterval);
      },
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange(`/feed-settings/filters/filters-list/${templateCreatedId}`);
        routeChange(`/feed-settings/filters`, {
          state: { templateId: templateCreatedId },
        });
      }
    });

  let navigate = useNavigate();
  const routeChange = (path, options) => {
    navigate(path, options);
  };

  function checkPermissions(permissions) {
    if (permissions.indexOf("management-platforms-create") > -1) setAllowCreate(true);
    if (permissions.indexOf("management-platforms-delete") > -1) setAllowDelete(true);
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  useEffect(() => {
    initData();
  }, [platform.label, sports, providers]);

  useEffect(() => {
    if (permissions) checkPermissions(permissions);
  }, [permissions]);

  useEffect(() => {
    return () => {
      // console.log("living page...");
      if (abortController) {
        // console.log("abortController landing page");
        abortController.abort();
      }
    };
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <CreatePlatform
        id={recordToDelete}
        platformName={platformNameToEdit}
        platformExchange={platformExchangeNameToEdit}
        vlcourtEmail={vlcourtEmailToEdit}
        vlcourtPassword={vlcourtPasswordToEdit}
        queueIdentifier={queueIdentifierToEdit}
        routingKey={routingKeyToEdit}
        rmqsSelected={rmqsSelectedToEdit}
        settleOnlyBets={settleOnlyBetsValue}
        validateBets={validateBetsValue}
        receiveSeperateStatistics={receiveSeperateStatisticsValue}
        riskManagement={riskManagementValue}
        demo={demoValue}
        hardProviderDown={hardProviderDownValue}
        nuRemove={nuRemoveValue}
        compressMessage={compressMessageValue}
        traderView={traderViewValue}
        promos={promosValue}
        outrights={outrightsValue}
        logo={logo}
        masterId={masterId}
        showFilters={allowCreate}
        showModal={showCreateModal}
        signalCloseModal={handleCloseModal}
      ></CreatePlatform>
      <CreatePlatformWizard
        showModal={showCreateWizardModal}
        signalCloseModal={handleCloseWizardModal}
      ></CreatePlatformWizard>
      <CheckObjectDeletion
        referenceId={recordToDelete}
        reference={"platforms"}
        showModal={showDeleteModal}
        signalCloseModal={handleDeleteCloseModal}
        action={modalCheckAction}
      ></CheckObjectDeletion>
      <SoftBox my={3}>
        <Card>
          <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" p={3}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                Your Platforms
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="regular" color="text">
                Here you can find all the saved Platforms you have
              </SoftTypography>
            </SoftBox>
            <Stack spacing={1} direction="row">
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                onClick={handleCreateRecord}
                style={{ display: allowCreate ? "" : "none" }}
              >
                + new Platform
              </SoftButton>
              <SoftButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={handleCreateWizardRecord}
                style={{ display: allowCreate ? "" : "none" }}
              >
                + platform wizard
              </SoftButton>
            </Stack>
          </SoftBox>
          <DataTable
            table={dataTableData}
            entriesPerPage={{
              defaultValue: 10,
              entries: [5, 10, 15, 20, 25],
            }}
            canSearch
            openBackDrop={loading}
          />
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}

export default PlatformsList;
